import { and, not, assign } from 'xstate';

import {
  CareType, MatchingProvidersType, updateMatchPreferences, updatePatientDetails
} from '@uplift/api';
import { PATIENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';

import { SELF_PAY_ID } from '../../utils';
import { MatchingFlowTypeEnum, OnboardingMachineContext } from '../types';
import { machineSetup } from './setup';
import { MATCHES_LOADING_DURATION, syncRouteInvocation } from './utils';

const initialContext: OnboardingMachineContext = {
  isSlotUnavailable: false,
  isLoggedIn: null,
  shouldShowWelcomeStep: true,
  userId: null,
  matchId: null,
  seed: null,
  stateId: null,
  insuranceId: null,
  who: null,
  modalityId: null,
  age: null,
  careType: null,
  recommendedCareType: null,
  isUnsureAboutCareType: false,
  skipCareType: false,
  specialtyIds: [],

  showTheseProviderFilters: [],
  identityFilters: {
    genderIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  experienceFilters: {
    communityIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  availabilityTimeOfDay: [],
  availabilityDayOfWeek: [],

  phqScore1: null,
  phqScore2: null,
  gadScore1: null,
  gadScore2: null,
  hasMedHistory: null,
  hasTherapist: null,
  isTherapistGoodFit: null,

  termsAccepted: false,

  selectedProviderId: null,
  appointment: null,
  appointmentProviderId: null,
  referrer: null,
  inviteToken: null,
  providerUrl: null,

  bookingBothInsuranceId: null,
  bookingBothSelectedProviderId: null,
  bookingBothMatchingPaymentType: null,
  bookingBothAppointment: null,

  matchingPaymentType: null,
  matchingFlow: MatchingFlowTypeEnum.Marketplace,
  progressPercentage: 0,

  intakeFormSubmissionId: null,
  isBetterhelpUser: null,
};

const syncContextDataToDatabase = async ({ context }: { context: OnboardingMachineContext }) => {
  console.log('Syncing context data to database:', context);

  if (context.isLoggedIn && context.userId && context.matchId) {
    await updateMatchPreferences(context.matchId, {
      stateId: context.stateId,
      appointmentType: context.careType,
      recommendedService: context.recommendedCareType,
      visitReasonIds: context.specialtyIds,
      insuranceCompanyId: context.insuranceId,
      modalityId: context.modalityId,
      ageGroupId: context.age?.id,
      faithIds: context.identityFilters.faithIds,
      ethnicityIds: context.identityFilters.ethnicityIds,
      genderIds: context.identityFilters.genderIds,
      communityExpertise: {
        faithIds: context.experienceFilters.faithIds,
        ethnicityIds: context.experienceFilters.ethnicityIds,
        generalIds: context.experienceFilters.communityIds,
      },
      directFlowData: context.providerUrl ? {
        providerId: context.selectedProviderId,
        appointment: context.appointment.fromDatetime,
        publicProfileUrl: context.providerUrl,
      } : null
    });

    await updatePatientDetails(context.userId, {
      onboardingCaretypeSelection: context.careType,
      onboardingRecommendedCaretype: context.recommendedCareType,
    });
  }
};


export const onboardingMachine = machineSetup.createMachine({
  id: 'matching',
  initial: 'Idle',
  invoke: { src: 'handleHistoryPop' },
  context: (): OnboardingMachineContext => initialContext,
  states: {
    Idle: {
      on: {
        Start: 'Initial'
      }
    },
    Initial: {
      invoke: {
        src: 'load',
        onError: {
          actions: 'redirectToLogin',
        },
        onDone: [
          {
            target: 'Direct',
            guard: ({ event }) => [ MatchingFlowTypeEnum.Direct, MatchingFlowTypeEnum.Invite ].includes(event.output.matchingFlow),
            actions: assign(({ event }) => event.output),
          },
          {
            target: 'Marketplace',
            actions: assign(({ event }) => ({
              ...event.output,
              matchingFlow: MatchingFlowTypeEnum.Marketplace,
            }))
          },
        ],
      }
    },

    /**
      * Marketplace flow starts here
      */
    Marketplace: {
      id: 'marketplace',
      initial: 'SelectStep',
      states: {
        SelectStep: {
          always: [
            {
              target: 'Welcome',
              guard: ({ context }) => !context.isLoggedIn && context.shouldShowWelcomeStep,
            },
            {
              target: 'Where1',
              guard: ({ context }) => !context.isLoggedIn && !context.shouldShowWelcomeStep && !context.stateId,
            },
            {
              target: 'Where',
              guard: ({ context }) => (
                context.isLoggedIn
                && !context.stateId
              ),
            },
            {
              target: 'Insurance',
              guard: ({ context }) => !context.insuranceId
            },
            {
              target: 'Who',
              guard: and([
                ({ context }) => (context.isLoggedIn && !context.who),
                not('isBetterHelpUser'),
              ]),
            },
            {
              target: 'Age',
              guard: ({ context }) => (
                context.isLoggedIn
                && !context.age
              ),
            },
            {
              target: 'Reason',
              guard: ({ context }) => (
                context.isLoggedIn
                && !context.specialtyIds.length
              ),
            },
            {
              target: 'LevelOfCare',
              guard: and([
                ({ context }) => (context.isLoggedIn && !context.careType),
                not('isBetterHelpUser'),
              ]),
            },
            {
              target: 'MatchesLoading',
              guard: ({ context }) => context.isLoggedIn,
            },
          ],
        },
        Welcome: {
          entry: assign({ progressPercentage: 0 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.welcome),
          on: {
            'Welcome.Next': 'Where'
          }
        },
        Where1: {
          entry: assign({ progressPercentage: 10 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.where1),
          on: {
            'Where.Next': {
              target: 'Insurance',
              actions: [
                assign({
                  stateId: ({ event }) => event.data.stateId,
                }),
                syncContextDataToDatabase,
              ]
            },
            'Where.NotAvailable': {
              target: 'NotAvailable',
              actions: [
                assign({
                  stateId: ({ event }) => event.data.stateId,
                }),
                syncContextDataToDatabase
              ]
            },
          }
        },
        Where: {
          entry: assign({ progressPercentage: 10 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.where),
          on: {
            'Where.Next': {
              target: 'Insurance',
              actions: [
                assign({
                  stateId: ({ event }) => event.data.stateId,
                }),
                syncContextDataToDatabase,
              ]
            },
            'Where.NotAvailable': {
              target: 'NotAvailable',
              actions: [
                assign({
                  stateId: ({ event }) => event.data.stateId,
                }),
                syncContextDataToDatabase
              ]
            },
            Back: 'Welcome'
          },
        },
        NotAvailable: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.notAvailable),
          on: {
            Back: [
              {
                target: 'Where',
                guard: ({ context }) => context.shouldShowWelcomeStep,
              },
              {
                target: 'Where1',
              }
            ]
          }
        },
        Insurance: {
          entry: assign({ progressPercentage: 20 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.insurance),
          on: {
            'Insurance.Next': [
              {
                target: 'WhoAutoCalculate',
                guard: 'isBetterHelpUser',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase
                ]
              },
              {
                target: 'Who',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase
                ]
              },
            ],
            Back: [
              {
                target: 'Where',
                guard: ({ context }) => context.shouldShowWelcomeStep,
              },
              {
                target: 'Where1',
              }
            ]
          }
        },
        WhoAutoCalculate: {
          tags: 'loading',
          invoke: {
            src: 'autoCalculateWho',
            onDone: {
              target: 'Age',
              actions: [
                assign({
                  who: ({ event }) => event.output.who,
                  modalityId: ({ event }) => event.output.modalityId
                }),
                syncContextDataToDatabase,
              ]
            },
          },
        },
        Who: {
          entry: assign({ progressPercentage: 30 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.who),
          on: {
            'Who.Next': [
              {
                target: 'Reason',
                guard: 'isBookingForMultiplePeople',
                actions: [
                  assign({
                    who: ({ event }) => event.data.who,
                    modalityId: ({ event }) => event.data.modalityId
                  }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'Age',
                actions: [
                  assign({
                    who: ({ event }) => event.data.who,
                    modalityId: ({ event }) => event.data.modalityId
                  }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: {
              target: 'Insurance',
              actions: assign({
                who: null,
                modalityId: null
              })
            }
          },

        },
        Age: {
          entry: assign({ progressPercentage: 35 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.age),
          on: {
            'Age.Next': [
              {
                target: 'TooYoung',
                guard: {
                  type: 'isTooYoung',
                  params: ({ event }) => ({ age: event.data.age })
                }
              },
              {
                target: 'Reason',
                actions: [
                  assign({ age: ({ event }) => event.data.age }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: [
              {
                target: 'Insurance',
                guard: 'isBetterHelpUser',
                actions: assign({ age: null })
              },
              {
                target: 'Who',
                actions: assign({ age: null })
              }
            ]
          }
        },
        TooYoung: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.tooYoung),
          on: { Back: 'Age' }
        },
        Reason: {
          entry: assign({ progressPercentage: 40 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.reason),
          on: {
            'Reason.Next': [
              {
                target: 'ProviderFilters',
                guard: 'isBookingForMultiplePeople',
                actions: [
                  assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'ProviderFilters',
                guard: 'isBetterHelpUser',
                actions: [
                  assign({
                    careType: () => CareType.psychiatry,
                  }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'LevelOfCareEntry',
                actions: [
                  assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: [
              {
                target: 'Who',
                guard: 'isBookingForMultiplePeople',
              },
              'Age'
            ]
          }
        },
        // step that will check if there are psychiatrists in the selected state
        // if there are no psychiatrists, we will skip the level of care step and continue with talk therapy as the levelOfCare value
        // also set skipLevelOfCare to true so we know to skip the level of care step when going back from future steps
        LevelOfCareEntry: {
          invoke: {
            src: 'getPsychiatristsInState',
            input: ({ context }) => ({ stateId: context.stateId }),
            onDone: [
              {
                target: 'ProviderFilters',
                guard: ({ event }) => !event.output.stateHasPsychiatrists,
                actions: assign({
                  careType: CareType.therapy,
                  skipCareType: true,
                }),
              },
              {
                target: 'LevelOfCare',
              },
            ],
            onError: {
              target: 'LevelOfCare'
            }
          }
        },
        LevelOfCare: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.levelOfCare),
          entry: assign({ isUnsureAboutCareType: false, careType: null, progressPercentage: 50 }),
          on: {
            'LevelOfCare.Next': [
              {
                target: 'Assessment',
                guard: ({ event }) => event.data.careType === null,
                actions: [
                  assign({ isUnsureAboutCareType: true, careType: null }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'ProviderFilters',
                guard: ({ event }) => event.data.careType === CareType.therapy || event.data.careType === CareType.therapyAndPsychiatry,
                actions: [
                  assign({ careType: ({ event }) => event.data.careType }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'CreateAccount',
                guard: ({ event }) => event.data.careType === CareType.psychiatry,
                actions: [
                  assign({ careType: CareType.psychiatry }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: 'Reason'
          }
        },
        Assessment: {
          initial: 'AssessmentEntry',
          states: {
            AssessmentEntry: {
              entry: assign({ progressPercentage: 51.5 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentEntry),
              on: {
                'AssessmentEntry.Next': 'AssessmentQuestion1',
                Back: '#marketplace.LevelOfCare'
              },
            },
            AssessmentQuestion1: {
              entry: assign({ progressPercentage: 53 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion1),
              on: {
                'AssessmentQuestion.Next': {
                  target: 'AssessmentQuestion2',
                  actions: assign({ phqScore1: ({ event }) => event.data.score })
                },
                Back: 'AssessmentEntry'
              },
            },
            AssessmentQuestion2: {
              entry: assign({ progressPercentage: 54.5 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion2),
              on: {
                'AssessmentQuestion.Next': {
                  target: 'AssessmentQuestion3',
                  actions: assign({ phqScore2: ({ event }) => event.data.score })
                },
                Back: 'AssessmentQuestion1'
              },
            },
            AssessmentQuestion3: {
              entry: assign({ progressPercentage: 56 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion3),
              on: {
                'AssessmentQuestion.Next': {
                  target: 'AssessmentQuestion4',
                  actions: assign({ gadScore1: ({ event }) => event.data.score })
                },
                Back: 'AssessmentQuestion2'
              },
            },
            AssessmentQuestion4: {
              entry: assign({ progressPercentage: 57.5 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion4),
              on: {
                'AssessmentQuestion.Next': {
                  target: 'AssessmentMedHistoryQuestion',
                  actions: assign({ gadScore2: ({ event }) => event.data.score })
                },
                Back: 'AssessmentQuestion3'
              },
            },
            AssessmentMedHistoryQuestion: {
              // entry: assign({ progressPercentage:  }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentMedHistory),
              on: {
                'AssessmentYesNoQuestion.Next': {
                  target: 'AssessmentHasTherapistQuestion',
                  actions: assign({ hasMedHistory: ({ event }) => event.data.answer })
                },
                Back: 'AssessmentQuestion4'
              }
            },
            AssessmentHasTherapistQuestion: {
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentHasTherapist),
              on: {
                'AssessmentYesNoQuestion.Next': [
                  {
                    target: 'AssessmentIsTherapistGoodFitQuestion',
                    guard: ({ event }) => event.data.answer === true,
                    actions: assign({ hasTherapist: true })
                  },
                  {
                    target: 'AssessmentResult',
                    actions: assign({
                      hasTherapist: ({ event }) => event.data.answer,
                      isTherapistGoodFit: null,
                    })
                  }
                ],
                Back: 'AssessmentMedHistoryQuestion'
              }
            },
            AssessmentIsTherapistGoodFitQuestion: {
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentIsTherapistGoodFit),
              on: {
                'AssessmentYesNoQuestion.Next': {
                  target: 'AssessmentResult',
                  actions: assign({ isTherapistGoodFit: ({ event }) => event.data.answer })
                },
                Back: 'AssessmentHasTherapistQuestion'
              }
            },
            AssessmentResult: {
              entry: assign({ progressPercentage: 59 }),
              invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentResult),
              on: {
                'AssessmentResult.ChooseAnother': {
                  target: '#marketplace.LevelOfCare',
                  actions: assign({
                    isUnsureAboutCareType: false,
                    careType: null,
                    phqScore1: null,
                    phqScore2: null,
                    gadScore1: null,
                    gadScore2: null
                  })
                },
                'AssessmentResult.Next': [
                  {
                    target: '#marketplace.ProviderFilters',
                    actions: [
                      assign({
                        careType: ({ event }) => event.data.careType,
                        recommendedCareType: ({ event }) => event.data.careType,
                      }),
                      syncContextDataToDatabase
                    ],
                    guard: ({ event }) => event.data.careType === CareType.therapy || event.data.careType === CareType.therapyAndPsychiatry
                  },
                  {
                    target: '#marketplace.CreateAccount',
                    actions: [ assign({
                      careType: ({ event }) => event.data.careType,
                      recommendedCareType: ({ event }) => event.data.careType,
                    }),
                    syncContextDataToDatabase,
                    ],
                    guard: ({ event }) => event.data.careType === CareType.psychiatry
                  }
                ],
                Back: [
                  {
                    target: 'AssessmentIsTherapistGoodFitQuestion',
                    guard: ({ context }) => context.hasTherapist === true,
                  },
                  {
                    target: 'AssessmentHasTherapistQuestion'
                  }
                ]
              },
            },
          },
        },
        ProviderFilters: {
          entry: assign({ progressPercentage: 60.5 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.providerFilters),
          on: {
            'ProviderFilters.Next': [
              {
                target: 'ProviderIdentityFilter',
                guard: 'showProviderIdentityFilter',
                actions: [
                  assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters }),
                  syncContextDataToDatabase
                ]
              },
              {
                target: 'ProviderExperienceFilter',
                guard: 'showProviderExperienceFilter',
                actions: [
                  assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters }),
                  syncContextDataToDatabase
                ]

              },
              {
                target: 'CreateAccount',
                actions: [
                  assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters }),
                  syncContextDataToDatabase
                ]
              }
            ],
            Back: [
              {
                target: 'Reason',
                guard: ({ context }) => context.skipCareType,
              },
              {
                target: 'Reason',
                guard: 'isBetterHelpUser',
              },
              {
                target: 'LevelOfCare',
                guard: ({ context }) => !context.isUnsureAboutCareType && (context.careType === CareType.therapy || context.careType === CareType.therapyAndPsychiatry)
              },
              {
                target: '#marketplace.Assessment.AssessmentResult',
                guard: ({ context }) => context.isUnsureAboutCareType && (context.careType === CareType.therapy || context.careType === CareType.therapyAndPsychiatry)
              },
              'Reason'
            ]
          }
        },
        ProviderIdentityFilter: {
          entry: assign({ progressPercentage: 62 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter),
          on: {
            'ProviderIdentityFilter.Next': [
              {
                target: 'ProviderExperienceFilter',
                guard: 'showProviderExperienceFilter',
                actions: [
                  assign({ identityFilters: ({ event }) => event.data }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'CreateAccount',
                actions: [
                  assign({ identityFilters: ({ event }) => event.data }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: 'ProviderFilters'
          }
        },
        ProviderExperienceFilter: {
          entry: assign({ progressPercentage: 63 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter),
          on: {
            'ProviderExperienceFilter.Next': {
              target: 'CreateAccount',
              actions: [
                assign({ experienceFilters: ({ event }) => event.data }),
                syncContextDataToDatabase,
              ]
            },
            Back: [
              {
                target: 'ProviderIdentityFilter',
                guard: 'showProviderIdentityFilter',
              },
              'ProviderFilters'
            ]
          }
        },
        CreateAccount: {
          entry: assign({ progressPercentage: 65 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.createAccount),
          on: {
            'CreateAccount.Next': {
              target: 'MatchesLoading',
              actions: [
                assign(({ event }) => ({
                  matchId: event.data.matchId,
                  isLoggedIn: event.data.isLoggedIn,
                  userId: event.data.userId,
                })),
                syncContextDataToDatabase
              ]
            },
            Back: [
              {
                target: 'ProviderFilters',
                guard: and([
                  ({ context }) => !context.isUnsureAboutCareType && context.careType === CareType.psychiatry,
                  'isBetterHelpUser',
                ])
              },
              {
                target: 'LevelOfCare',
                guard: ({ context }) => !context.isUnsureAboutCareType && context.careType === CareType.psychiatry
              },
              {
                target: '#marketplace.Assessment.AssessmentResult',
                guard: ({ context }) => context.isUnsureAboutCareType && context.careType === CareType.psychiatry
              },
              {
                target: 'ProviderExperienceFilter',
                guard: 'showProviderExperienceFilter',
              },
              {
                target: 'ProviderIdentityFilter',
                guard: 'showProviderIdentityFilter',
              },
              'ProviderFilters'
            ]
          }
        },
        MatchesLoading: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.matchesLoading),
          after: { [MATCHES_LOADING_DURATION]: 'Matches' },
          on: { Back: 'CreateAccount' }
        },
        Matches: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.matches),
          on: {
            Back: 'CreateAccount',
            'Matches.UpdateFilters': {
              actions: [
                assign({
                  insuranceId: ({ event }) => event.data.insuranceId,
                  specialtyIds: ({ event }) => event.data.specialtyIds,
                  identityFilters: ({ event }) => event.data.identityFilters,
                  experienceFilters: ({ event }) => event.data.experienceFilters,
                  availabilityTimeOfDay: ({ event }) => event.data.availabilityTimeOfDay,
                  availabilityDayOfWeek: ({ event }) => event.data.availabilityDayOfWeek,
                }),
                syncContextDataToDatabase,
              ]
            },
            'Matches.SelectProvider': {
              target: 'Match',
              actions: [
                assign({
                  selectedProviderId: ({ event }) => event.data.providerId,
                  matchingPaymentType: ({ event }) => event.data.matchingPaymentType,
                }),
                syncContextDataToDatabase,
              ],
            }
          }
        },
        Match: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.match),
          on: {
            'Match.ScheduleSession': {
              target: 'AboutYou',
              actions: [
                assign({ appointment: ({ event }) => event.data.appointment }),
                syncContextDataToDatabase
              ]
            },
            Back: {
              target: 'Matches',
              actions: assign({ selectedProviderId: null })
            }
          }
        },
        AboutYou: {
          entry: assign({ progressPercentage: 70 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.aboutYou),
          on: {
            'AboutYou.Next': [
              {
                guard: ({ context }) => (
                  context.insuranceId === SELF_PAY_ID
                  || context.matchingPaymentType === MatchingProvidersType.cashpay
                ),
                target: 'PaymentInfo',
                actions: [ syncContextDataToDatabase ]
              },
              {
                target: 'InsuranceInfo',
                actions: [ syncContextDataToDatabase ]
              }
            ],
            Back: 'Match'
          },
        },
        InsuranceInfo: {
          entry: assign({ progressPercentage: 80 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.insuranceInfo),
          on: {
            'InsuranceInfo.Next': [
              {
                target: '#matching.ConfirmBooking',
                guard: 'isInsuranceMedicaid',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'PaymentInfo',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase,
                ]
              },
            ],
            Back: 'AboutYou'
          }
        },
        PaymentInfo: {
          entry: assign({ progressPercentage: 90 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.paymentInfo),
          on: {
            Back: [
              {
                guard: ({ context }) => context.insuranceId === SELF_PAY_ID,
                target: 'AboutYou',
              },
              'InsuranceInfo'
            ],
            'PaymentInfo.Next': {
              target: '#matching.ConfirmBooking',
              actions: [ syncContextDataToDatabase ]
            }
          }
        },
      }
    },

    /**
       * Direct flow starts here
       */
    Direct: {
      id: 'direct',
      initial: 'Where',
      states: {
        Where: {
          entry: assign({ progressPercentage: 20 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.where),
          on: {
            'Where.Next': {
              target: 'CreateAccount',
              actions: [
                assign({ stateId: ({ event }) => event.data.stateId }),
                syncContextDataToDatabase,
              ]
            },
            'Where.SwitchToMarketplace': {
              target: '#matching.Marketplace.Insurance',
              actions: [
                assign({
                  ...initialContext,
                  matchingFlow: MatchingFlowTypeEnum.Marketplace,
                  stateId: ({ event }) => event.data.stateId,
                }),
                syncContextDataToDatabase
              ]
            }
          }
        },
        CreateAccount: {
          entry: assign({ progressPercentage: 30 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.createAccount),
          on: {
            'CreateAccount.Next': {
              target: 'Who',
              actions: [
                assign(({ event, context }) => ({
                  userId: event.data.userId ?? context.userId,
                  matchId: event.data.matchId,
                  isLoggedIn: event.data.isLoggedIn,
                })),
                syncContextDataToDatabase
              ]
            },
            Back: 'Where'
          }
        },
        Who: {
          entry: assign({ progressPercentage: 40 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.who),
          on: {
            'Who.Next': [
              {
                target: 'AboutYou',
                guard: 'isBookingForMultiplePeople',
                actions: [
                  assign({
                    who: ({ event }) => event.data.who,
                    modalityId: ({ event }) => event.data.modalityId,
                  }),
                  syncContextDataToDatabase
                ]
              },
              {
                target: 'Age',
                guard: 'isBookingForSomeoneElse',
                actions: [
                  assign({
                    who: ({ event }) => event.data.who,
                    modalityId: ({ event }) => event.data.modalityId,
                  }),
                  syncContextDataToDatabase
                ]
              },
              {
                target: 'Reason',
                actions: [
                  assign({
                    who: ({ event }) => event.data.who,
                    modalityId: ({ event }) => event.data.modalityId,
                  }),
                  syncContextDataToDatabase
                ]
              }
            ],
            Back: 'CreateAccount'
          }
        },
        Age: {
          entry: assign({ progressPercentage: 50 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.age),
          on: {
            'Age.Next': [
              {
                target: 'TooYoung',
                guard: 'isTooYoung',
                actions: [
                  assign({ age: ({ event }) => event.data.age }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'Reason',
                actions: [
                  assign({ age: ({ event }) => event.data.age }),
                  syncContextDataToDatabase,
                ]
              }
            ],
            Back: 'Who'
          },
        },
        TooYoung: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.tooYoung),
          on: { Back: 'Age' }
        },
        Reason: {
          entry: assign({ progressPercentage: 60 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.reason),
          on: {
            'Reason.Next': {
              target: 'AboutYou',
              actions: [
                assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
                syncContextDataToDatabase,
              ]
            },
            Back: [
              {
                target: 'Age',
                guard: 'isBookingForSomeoneElse'
              },
              'Who'
            ]
          }
        },
        Match: {
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.match),
          on: {
            'Match.ScheduleSession': {
              target: '#matching.ConfirmBooking',
              actions: assign({
                appointment: ({ event }) => event.data.appointment,
                isSlotUnavailable: false,
              })
            },
          }
        },
        AboutYou: {
          entry: assign({ progressPercentage: 70 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.aboutYou),
          on: {
            'AboutYou.Next': [
              {
                guard: ({ context }) => (
                  context.insuranceId === SELF_PAY_ID
                  || context.matchingPaymentType === MatchingProvidersType.cashpay
                ),
                target: 'PaymentInfo'
              },
              'InsuranceInfo'
            ],
            Back: [
              {
                guard: 'isBookingForMultiplePeople',
                target: 'Who',
              },
              'Reason'
            ]
          }
        },
        InsuranceInfo: {
          entry: assign({ progressPercentage: 80 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.insuranceInfo),
          on: {
            'InsuranceInfo.Next': [
              {
                target: '#matching.ConfirmBooking',
                guard: 'isInsuranceMedicaid',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase,
                ]
              },
              {
                target: 'PaymentInfo',
                actions: [
                  assign({ insuranceId: ({ event }) => event.data.insuranceId }),
                  syncContextDataToDatabase,
                ]
              },
            ],
            Back: 'AboutYou'
          }
        },
        PaymentInfo: {
          entry: assign({ progressPercentage: 90 }),
          invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.paymentInfo),
          on: {
            Back: 'InsuranceInfo',
            'PaymentInfo.Next': '#matching.ConfirmBooking'
          }
        },
      },
    },

    ConfirmBooking: {
      entry: assign({ progressPercentage: 95 }),
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.confirmBooking),
      on: {
        Back: [
          {
            guard: and([ 'isInsuranceMedicaid', 'isDirect' ]),
            target: 'Direct.InsuranceInfo',
          },
          {
            guard: 'isInsuranceMedicaid',
            target: 'Marketplace.InsuranceInfo'
          },
          {
            guard: 'isDirect',
            target: 'Direct.PaymentInfo'
          },
          'Marketplace.PaymentInfo'
        ],
        'ConfirmBooking.Next': [
          {
            target: 'PsychInterstitial',
            guard: ({ context }) => context.careType === CareType.therapyAndPsychiatry,
            actions: [
              assign({
                termsAccepted: true,
                intakeFormSubmissionId: ({ event }) => event.data.intakeFormSubmissionId,
                bookingBothInsuranceId: ({ context }) => context.insuranceId
              }),
              syncContextDataToDatabase,
            ]
          },
          {
            target: 'BookingSuccess',
            actions: [
              assign({
                termsAccepted: true,
                intakeFormSubmissionId: ({ event }) => event.data.intakeFormSubmissionId
              }),
              syncContextDataToDatabase,
            ]
          }
        ],
        'ConfirmBooking.BookingConflict': {
          target: 'Direct.Match',
          actions: assign({ isSlotUnavailable: true }),
        },
      }
    },
    PsychInterstitial: {
      entry: assign({ progressPercentage: 95 }),
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.psychInterstitial, { replace: true }),
      on: {
        'PsychInterstitial.Next': 'BookingBothPsychMatches',
      }
    },
    BookingBothPsychMatches: {
      entry: assign({ progressPercentage: 97 }),
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatches),
      on: {
        'Matches.UpdateFilters': {
          actions: [
            assign({ bookingBothInsuranceId: ({ event }) => event.data.insuranceId }),
            syncContextDataToDatabase,
          ]
        },
        'Matches.SelectProvider': {
          target: 'BookingBothPsychMatch',
          actions: assign({
            bookingBothSelectedProviderId: ({ event }) => event.data.providerId,
            bookingBothMatchingPaymentType: ({ event }) => event.data.matchingPaymentType,
          }),
        },
        'Back': 'PsychInterstitial'
      }
    },
    BookingBothPsychMatch: {
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatch),
      on: {
        'Match.ScheduleSession': {
          target: 'BookingBothBookPsych',
          actions: assign({
            bookingBothAppointment: ({ event }) => event.data.appointment,
          })
        },
        'Back': 'BookingBothPsychMatches'
      }
    },
    BookingBothBookPsych: {
      tags: 'loading',
      invoke: {
        src: 'bookingBothBookPsych',
        input: ({ context }) => ({
          appointment: context.bookingBothAppointment,
          providerId: context.bookingBothSelectedProviderId,
          userId: context.userId,
        }),
        onDone: [
          {
            guard: ({ context }) => (
              context.bookingBothInsuranceId !== SELF_PAY_ID
              && context.matchingPaymentType !== MatchingProvidersType.insurance
              && context.bookingBothMatchingPaymentType === MatchingProvidersType.insurance
            ),
            target: 'BookingBothPsychInsuranceInfo'
          },
          'BookingSuccess'
        ],
        onError: {
          target: 'BookingBothPsychMatches',
          actions: assign({ isSlotUnavailable: true }),
        }
      }
    },
    BookingBothPsychInsuranceInfo: {
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychInsurance),
      on: {
        'InsuranceInfo.Next': 'BookingSuccess',
        Back: 'BookingBothBookPsych'
      }
    },
    BookingSuccess: {
      entry: assign({ progressPercentage: 100 }),
      invoke: syncRouteInvocation(PATIENT_ONBOARDING_ROUTING_CONFIG.bookingSuccess, { replace: true }),
    }
  }
});
